import {Answer} from "../../qnr/Questionnaire";
import UserEvent from "../../core/UserEvent";
import {Divider, Grid, Typography, useTheme} from "@material-ui/core";
import Box from "../Box";
import Forms, {AddAction, RemoveAction} from "../Forms";
import Button from "../Button";
import Space from "../Space";
import i18n from "../../core/i18n";
import React from "react";
import UniversalField from "../UniversalField";
import Form from "../Form";
import lang from "../../core/lang";
import PropTypes from "prop-types";
import {Questionnaire} from "../../qnr";
import VisibilitySensor from "react-visibility-sensor";
import {Skeleton} from "@material-ui/lab";
import CircleIndicator from "./CircleIndicator";
import {Icon} from "@iconify/react";
import roundRestartAlt from "@iconify/icons-ic/round-restart-alt";
import roundAdd from "@iconify/icons-ic/round-add";
import roundClear from "@iconify/icons-ic/round-clear";

function defaultChangeIndicatorRenderer(sequence) {
    return <CircleIndicator sequence={sequence} />
}

function defaultTailIndicatorRenderer() {
    return null;
}

function OEQuestion(props) {
    const {value, change, index, validation, disabledColor, diff, showUndo, variant} = props;
    const question = change || value;
    const answer = question.answer;
    const {description, helperText, required} = question;
    const disabled = (question.disabled || props.disabled);
    const readOnly = props.readOnly;
    const opacity = (props.readOnly && !question.disabled) ? 1 : undefined;
    const theme = useTheme();
    const [isVisible, setIsVisible] = React.useState(false);

    const handleChange = event => {
        if(event.bounce) return;
        const value = event.value;
        const newAnswer = new Answer({value});
        const newQuestion = question.clone();
        newQuestion.answer = newAnswer;
        props.onChange && props.onChange(new UserEvent({value: newQuestion}))
    }

    const handleUndo = () => {
        props.onChange && props.onChange(new UserEvent({value}))
    }

    let answerField;
    if(question.format) {
        const {multiple, fields, examples} = question.format;
        if(multiple) {
            answerField = <OeqForms disabled={disabled} readOnly={readOnly} fields={fields} value={answer?.value} examples={examples} onChange={handleChange} disabledColor={disabledColor} variant={variant} />
        } else {
            answerField = <OeqForm disabled={disabled} readOnly={readOnly} fields={fields} value={answer?.value} examples={examples} onChange={handleChange} disabledColor={disabledColor} />
        }

    } else {
        answerField = <UniversalField debounce disabled={disabled} readOnly={readOnly} type="text" variant="outlined" onChange={handleChange} size="small" value={answer?.value || ''} fullWidth />;
    }

    const indicator = (required && !question.disabled) ? '*' : '';

    const helperTextNode = disabled ? null : (
        <>
            <Space spacing={0.5} vertical />
            <Typography variant="caption" display="block">{helperText}</Typography>
        </>
    )

    const warning = validation && !disabled && !question.isCompleted();
    const color = disabled ? disabledColor : 'unset';
    const isChanged = !(lang.isNullOrUndefined(answer, value.answer) || lang.isEqual(answer, value.answer));
    const undo = (showUndo && !readOnly && !disabled && isChanged) ? <Button variant="icon" icon={<Icon icon={roundRestartAlt} />} size="small" onClick={handleUndo} /> : null;
    const changeIndicatorRenderer = props.changeIndicator || defaultChangeIndicatorRenderer;
    const changeIndicator = diff && !disabled && isChanged ? changeIndicatorRenderer(index) : null;
    const tailIndicatorRender = props.tailIndicatorRenderer || defaultTailIndicatorRenderer;
    const tailIndicator = tailIndicatorRender({isChanged, isDisabled: disabled, isReadOnly: readOnly});

    return (
        <VisibilitySensor onChange={isVisible => isVisible && setIsVisible(isVisible)}
            partialVisibility>
            {isVisible ? <Box disabled={disabled} opacity={opacity} color={color} paddingX={1}>
                <Box display="flex">
                    <Box position="relative" display="flex">
                        <Typography color={warning ? 'error' : 'initial'}>{`${index+1}.`}</Typography>
                        {changeIndicator}
                    </Box>
                    <Typography color={warning ? 'error' : 'initial'}>{`${description}`}{indicator}</Typography>
                    {undo}
                    {tailIndicator}
                </Box>
                <Box marginY={1}>
                    {answerField}
                    {helperTextNode}
                </Box>
            </Box> : <Skeleton animation="wave" />}
        </VisibilitySensor>
    )
}

const DEFAULTLAYOUTS = {
    'text': {
        flex: 3,
        addition: 2
    },
    'number': {
        flex: 2,
        addition: 0
    },
    'boolean': {
        flex: 2,
        addition: 0
    },
    'date': {
        flex: 3,
        addition: 1
    },
    'time': {
        flex: 3,
        addition: 1
    },
    'datetime': {
        flex: 4,
        addition: 1
    }
}

const STANDARDLAYOUT = {
    flex: 2,
    addition: 1
}

const LARGELAYOUT = {
    flex: 4,
    addition: 2
}

function layout(fields) {
    const template = [];
    const rows = [];

    let sum = 0, start = 0;
    for(const [index, field] of fields.entries()) {
        let layout;
        if(field.flex) {
            layout = {
                flex: field.flex,
                addition: 0
            }
        } else {
            if(field.multiple) {
                layout = LARGELAYOUT;
            } else {
                layout = DEFAULTLAYOUTS[field.type] || STANDARDLAYOUT;
            }
        }

        template[index] = layout.flex;
        if(sum + template[index] > 12) {
            rows.push({start, end: index -1, sum});
            sum = template[index];
            start = index;
        } else {
            sum += template[index];
        }
    }

    if(sum>0) {
        rows.push({start, end: fields.length - 1, sum});
    }

    for(const {start, end, sum} of rows) {
        let rest = 12 - sum;
        let index = end;
        while(rest > 0) {
            const field = fields[index];
            let layout;
            if(field.multiple) {
                layout = LARGELAYOUT;
            } else {
                layout = DEFAULTLAYOUTS[field.type] || STANDARDLAYOUT;
            }
            const addition = Math.min(layout.addition, rest);
            template[index] += addition;
            rest -= addition;
            if(index===start) {
                index = end;
            } else {
                index--;
            }
        }
    }

    const breakpoints = rows.map(row => row.end);

    return {template, breakpoints};
}

function OeqForms(props) {
    const {fields, value, examples, disabled, readOnly, disabledColor, variant} = props;
    const {template, breakpoints} = layout(fields);

    const color = disabled ? disabledColor : undefined;
    const handleChange = event => {
        const {value = []} = event;
        let currValue;
        currValue = (value.length===0) ? [] : value;
        props.onChange && props.onChange(new UserEvent({value: currValue}, event));
    }

    const buttons = (
        <Box display="flex" gap={1} alignItems="center" fullHeight disabled={disabled || readOnly}>
            <AddAction><Button icon={<Icon icon={roundAdd} />} size="small" variant="icon" /></AddAction>
            <RemoveAction><Button icon={<Icon icon={roundClear} />} size="small" variant="icon" /></RemoveAction>
        </Box>
    )
    const firstBreakpoint = breakpoints[0];
    const oeqHeaders = fields.slice(0, firstBreakpoint+1).map((field, index) => {
        const {name} = field;
        const flex = template[index];
        return (
            <Grid item xs={flex} key={index}>
                <Box display="flex" justifyContent="center">
                    <Typography align="center">{name}</Typography>
                </Box>
            </Grid>
        )
    });

    const exampleFields = fields.map((field, index) => {
        const {type, required, name, multiple, min, max, step, minlength, maxlength, pattern} = field;
        const flex = template[index];
        const label = index > firstBreakpoint ? name : undefined;
        return (
            <Grid item xs={flex} key={index}>
                <Box display="flex">
                    <UniversalField type={type} label={label} required={required} name={name} variant="outlined" size="small" fullWidth
                                    multiple={multiple} min={min} max={max} step={step} minlength={minlength} maxlength={maxlength} pattern={pattern} color={color}/>
                </Box>
            </Grid>
        );
    });

    const oeqFields = fields.map((field, index) => {
        const {type, required, name, multiple, min, max, step, minlength, maxlength, pattern, placeholder, helperText} = field;
        const flex = template[index];
        const label = index > firstBreakpoint ? name : undefined;
        return (
            <Grid item xs={flex} key={index}>
                <Box display="flex">
                    <UniversalField type={type} label={label} required={required} name={name} variant="outlined" size="small" fullWidth
                                    multiple={multiple} min={min} max={max} step={step} minlength={minlength} maxlength={maxlength} pattern={pattern}
                                    placeholder={placeholder} helperText={helperText} color={color} />
                </Box>
            </Grid>
        );
    });

    const examplesPart = (
        <Forms value={examples} divider={false} gap={2} readOnly disabled={disabled} color={color}>
            <Box display="flex" gap={1} alignItems="center">
                <Grid container spacing={2}>
                    {exampleFields}
                </Grid>
                <Box width="4rem" color="slategrey">
                    <Typography align="center" noWrap color="initial">{i18n.translate('Example')}</Typography>
                </Box>
            </Box>
        </Forms>
    );

    const divider = (
        <Box fullWidth>
            <Divider />
        </Box>
    )

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" gap={1} alignItems="center" color={color}>
                <Grid container spacing={2}>
                    {oeqHeaders}
                </Grid>
                <Box visibility="hidden">
                    {buttons}
                </Box>
            </Box>
            {divider}
            {lang.isNotEmpty(examples) && variant==='legacy' ? examplesPart : null}
            <Forms onChange={handleChange} value={value} divider={false} gap={1} readOnly={readOnly} disabled={disabled} color={color}>
                <Box display="flex" gap={1} alignItems="center">
                    <Grid container spacing={2}>
                        {oeqFields}
                    </Grid>
                    {buttons}
                </Box>
            </Forms>
            {lang.isNotEmpty(examples) && variant!=='legacy' ? divider : null}
            {lang.isNotEmpty(examples) && variant!=='legacy' ? examplesPart : null}
        </Box>
    )
}

function OeqForm(props) {
    const {fields, value, disabled, readOnly, disabledColor} = props;
    const {template} = layout(fields);

    const color = disabled ? disabledColor : undefined;
    const handleChange = event => {
        const {value = {}} = event;
        props.onChange && props.onChange(new UserEvent({value}, event));
    }

    const oeqFields = fields.map((field, index) => {
        const {type, required, name, multiple, min, max, step, minlength, maxlength, pattern, placeholder, helperText} = field;
        const flex = template[index];
        return (
            <Grid item xs={flex} key={index}>
                <UniversalField type={type} label={name} required={required} name={name} variant="outlined" size="small" fullWidth
                                placeholder={placeholder} helperText={helperText}
                                multiple={multiple} min={min} max={max} step={step} minlength={minlength} maxlength={maxlength} pattern={pattern}
                                color={color} />
            </Grid>
        );
    });

    return (
        <Form onChange={handleChange} value={value} disabled={disabled} readOnly={readOnly} color={color}>
            <Grid container spacing={2}>
                {oeqFields}
            </Grid>
        </Form>
    )
}

OEQuestion.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.instanceOf(Questionnaire.Question)
    ]),
    index: PropTypes.number,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    // 是否檢視檢核結果
    validation: PropTypes.bool,
    disabledColor: PropTypes.string,
    // 標示變更的差異
    diff: PropTypes.bool,
    showUndo: PropTypes.bool,
    variant: PropTypes.oneOf(['legacy', 'classic']),
    changeIndicator: PropTypes.func,
    tailIndicatorRenderer: PropTypes.func,
}

OEQuestion.defaultProps = {
    variant: 'classic',
    showUndo: true
}

export default OEQuestion;